/**
 * API Key Settings Page - API-Schlüssel Verwaltung
 * Optimiert für alle Geräte mit responsivem Design
 */

import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Stack,
  Grid,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  CardActions,
  Divider,
  useTheme,
  useMediaQuery,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  VpnKey as ApiKeyIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  AccessTime as ExpiresIcon,
  Person as CreatedByIcon,
} from "@mui/icons-material";
import { apiClient } from "../../services/apiClient";
import { ApiKey, CreateApiKeyRequest } from "../../types/api-types";
import LoadingSpinner from "../../components/Common/LoadingSpinner";

const ApiKeySettingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    apiKey: ApiKey | null;
  }>({
    open: false,
    apiKey: null,
  });
  const [newKeyDialog, setNewKeyDialog] = useState<{
    open: boolean;
    apiKey: ApiKey | null;
  }>({
    open: false,
    apiKey: null,
  });

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permissions: [] as string[],
    expiresAt: "",
    expiresEnabled: false,
  });

  // Available permissions
  const availablePermissions = [
    { value: "status", label: "Geschäftsdaten lesen" },
    { value: "opening_hours", label: "Öffnungszeiten lesen" },
    { value: "catalog", label: "Artikel-Katalog lesen" },
  ];

  // Load API keys
  const loadApiKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const keys = await apiClient.getApiKeys();
      setApiKeys(keys);
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der API-Schlüssel");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApiKeys();
  }, []);

  const handleAddApiKey = () => {
    setFormData({
      name: "",
      description: "",
      permissions: [],
      expiresAt: "",
      expiresEnabled: false,
    });
    setShowForm(true);
  };

  const handleDeleteApiKey = (apiKey: ApiKey) => {
    setDeleteDialog({ open: true, apiKey });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.apiKey) return;

    try {
      await apiClient.deactivateApiKey(deleteDialog.apiKey.id);
      setApiKeys(apiKeys.filter((k) => k.id !== deleteDialog.apiKey!.id));
      setDeleteDialog({ open: false, apiKey: null });
    } catch (err: any) {
      setError(err.message || "Fehler beim Deaktivieren des API-Schlüssels");
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const request: CreateApiKeyRequest = {
        name: formData.name,
        description: formData.description || undefined,
        permissions: formData.permissions,
        expiresAt:
          formData.expiresEnabled && formData.expiresAt
            ? new Date(formData.expiresAt)
            : undefined,
      };

      const newApiKey = await apiClient.createApiKey(request);
      setApiKeys([newApiKey, ...apiKeys]);
      setShowForm(false);
      setNewKeyDialog({ open: true, apiKey: newApiKey });
    } catch (err: any) {
      setError(err.message || "Fehler beim Erstellen des API-Schlüssels");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePermissionChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setFormData((prev) => ({
      ...prev,
      permissions: value,
    }));
  };

  const handleCopyKey = async (keyValue: string) => {
    try {
      await navigator.clipboard.writeText(keyValue);
      // You could add a toast notification here
    } catch (err) {
      console.error("Fehler beim Kopieren:", err);
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return "Nie";

    let dateObj: Date;
    if (typeof date === "string") {
      dateObj = new Date(date);
    } else {
      dateObj = date;
    }

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return "Ungültiges Datum";
    }

    return dateObj.toLocaleDateString("de-DE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPermissionLabel = (permission: string) => {
    const perm = availablePermissions.find((p) => p.value === permission);
    return perm ? perm.label : permission;
  };

  if (loading) {
    return <LoadingSpinner text="Lade API-Schlüssel..." />;
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 0.5, md: 3 },
        maxWidth: { xs: "100%", md: "xl" },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <ApiKeyIcon fontSize={isMobile ? "medium" : "large"} />
              API-Schlüssel Verwaltung
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Verwalten Sie API-Schlüssel für den externen Zugriff auf Ihre
              Daten
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddApiKey}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              borderRadius: 2,
            }}
          >
            Neuer Schlüssel
          </Button>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* API Keys List */}
      <Grid container spacing={3}>
        {apiKeys.map((apiKey) => (
          <Grid item xs={12} md={6} lg={4} key={apiKey.id}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    {apiKey.name}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {apiKey.isActive ? (
                      <Chip
                        icon={<ActiveIcon />}
                        label="Aktiv"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={<InactiveIcon />}
                        label="Inaktiv"
                        color="error"
                        size="small"
                      />
                    )}
                  </Box>
                </Box>

                {apiKey.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {apiKey.description}
                  </Typography>
                )}

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    Berechtigungen:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {apiKey.permissions.map((permission) => (
                      <Chip
                        key={permission}
                        label={getPermissionLabel(permission)}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ExpiresIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Erstellt: {formatDate(apiKey.createdAt)}
                    </Typography>
                  </Box>

                  {apiKey.expiresAt && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ExpiresIcon
                        sx={{ fontSize: 16, color: "warning.main" }}
                      />
                      <Typography variant="body2" color="warning.main">
                        Läuft ab: {formatDate(apiKey.expiresAt)}
                      </Typography>
                    </Box>
                  )}

                  {apiKey.lastUsed && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <RefreshIcon
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Zuletzt verwendet: {formatDate(apiKey.lastUsed)}
                      </Typography>
                    </Box>
                  )}

                  <Typography variant="body2" color="text.secondary">
                    Verwendungen: {apiKey.usageCount}
                  </Typography>
                </Stack>
              </CardContent>

              <CardActions
                sx={{
                  p: { xs: 2, md: 3 },
                  pt: 0,
                  justifyContent: "space-between",
                }}
              >
                <Tooltip title="API-Schlüssel kopieren">
                  <IconButton
                    onClick={() => handleCopyKey(apiKey.keyValue)}
                    color="primary"
                  >
                    <CopyIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="API-Schlüssel deaktivieren">
                  <IconButton
                    onClick={() => handleDeleteApiKey(apiKey)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {apiKeys.length === 0 && (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <ApiKeyIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Keine API-Schlüssel vorhanden
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Erstellen Sie Ihren ersten API-Schlüssel, um externen Zugriff auf
            Ihre Daten zu ermöglichen.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddApiKey}
          >
            Ersten API-Schlüssel erstellen
          </Button>
        </Paper>
      )}

      {/* Create API Key Dialog */}
      <Dialog
        open={showForm}
        onClose={() => setShowForm(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ApiKeyIcon />
            Neuer API-Schlüssel
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              fullWidth
              required
              placeholder="z.B. Webshop Integration"
            />

            <TextField
              label="Beschreibung"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Optionale Beschreibung des API-Schlüssels"
            />

            <FormControl fullWidth>
              <InputLabel>Berechtigungen</InputLabel>
              <Select
                multiple
                value={formData.permissions}
                onChange={handlePermissionChange}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip
                        key={value}
                        label={getPermissionLabel(value)}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {availablePermissions.map((permission) => (
                  <MenuItem key={permission.value} value={permission.value}>
                    {permission.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.expiresEnabled}
                    onChange={(e) =>
                      handleInputChange("expiresEnabled", e.target.checked)
                    }
                  />
                }
                label="Ablaufdatum festlegen"
              />

              {formData.expiresEnabled && (
                <TextField
                  label="Läuft ab am"
                  type="datetime-local"
                  value={formData.expiresAt}
                  onChange={(e) =>
                    handleInputChange("expiresAt", e.target.value)
                  }
                  fullWidth
                  sx={{ mt: 2 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setShowForm(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              saving || !formData.name || formData.permissions.length === 0
            }
          >
            {saving ? "Erstelle..." : "Erstellen"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Key Dialog */}
      <Dialog
        open={newKeyDialog.open}
        onClose={() => setNewKeyDialog({ open: false, apiKey: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ApiKeyIcon color="success" />
            API-Schlüssel erfolgreich erstellt
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {newKeyDialog.apiKey && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Alert severity="warning">
                <strong>Wichtig:</strong> Kopieren Sie den API-Schlüssel jetzt.
                Er wird nur einmal angezeigt und kann später nicht mehr
                abgerufen werden.
              </Alert>

              <Box>
                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                  API-Schlüssel:
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: "grey.100",
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    position: "relative",
                  }}
                >
                  {newKeyDialog.apiKey.keyValue}
                  <IconButton
                    onClick={() => handleCopyKey(newKeyDialog.apiKey!.keyValue)}
                    sx={{ position: "absolute", top: 8, right: 8 }}
                  >
                    <CopyIcon />
                  </IconButton>
                </Paper>
              </Box>

              <Box>
                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                  Verwendung:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fügen Sie diesen Schlüssel in den Authorization-Header Ihrer
                  API-Anfragen ein:
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: "grey.100",
                    fontFamily: "monospace",
                    mt: 1,
                  }}
                >
                  X-API-Key: {newKeyDialog.apiKey.keyValue}
                </Paper>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setNewKeyDialog({ open: false, apiKey: null })}
            variant="contained"
          >
            Verstanden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, apiKey: null })}
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>API-Schlüssel deaktivieren</DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography>
            Sind Sie sicher, dass Sie den API-Schlüssel "
            {deleteDialog.apiKey?.name}" deaktivieren möchten? Der Schlüssel
            kann nicht wieder aktiviert werden und alle damit verbundenen
            Anfragen werden abgelehnt.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, apiKey: null })}
          >
            Abbrechen
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Deaktivieren
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ApiKeySettingsPage;
